import type { APIResponse } from '../types';

class APIError extends Error {
  constructor(public code: string, message: string, public details?: unknown) {
    super(message);
    this.name = 'APIError';
  }
}

/**
 * Explain a non-JSON API response in words a person can act on (CIPP's
 * get-cipp-error does the same for its `<!DOCTYPE html>` fallback). These
 * responses never come from the worker's route handlers — they are edge
 * artifacts: a Cloudflare timeout/error page, an auth interstitial, or the SPA
 * shell served for an unknown path. Parsing them as JSON used to surface the
 * useless `Unexpected token '<', "<!DOCTYPE "... is not valid JSON`.
 */
function describeNonJsonResponse(status: number, body: string): string {
  const looksHtml = /<!doctype\s+html|<html[\s>]/i.test(body);
  const kind = looksHtml ? 'an HTML page' : 'a non-JSON response';
  if (status === 504 || status === 524 || status === 522) {
    return `The request timed out at the network edge before the API finished (HTTP ${status}). The operation may still be running in the background — wait a moment and refresh.`;
  }
  if (status >= 500) {
    return `The API returned ${kind} instead of JSON (HTTP ${status}). This usually means the worker crashed or the edge intercepted the request — check the worker logs and retry.`;
  }
  if (status === 403) {
    return `Access was blocked before reaching the API (HTTP 403). Your session may have expired — reload the page and sign in again.`;
  }
  return `The API returned ${kind} instead of JSON (HTTP ${status}). Reload the page and try again.`;
}

/**
 * The NZT error code behind a rejected API call, so a page can tell an expected
 * tenant condition apart from a real failure and present it accordingly.
 * Returns null for anything the API layer did not raise.
 */
export function errorCode(err: unknown): string | null {
  return err instanceof APIError ? err.code : null;
}

/**
 * Cache provenance the worker reports alongside slow Graph-backed data, so a
 * page can show how old what it is displaying is (see components/RefreshControl).
 */
export interface CacheMeta {
  /** True when the payload came from the worker's KV cache rather than live from Graph. */
  cached?: boolean;
  /** ISO timestamp of the underlying Graph fetch. */
  cached_at?: string;
  /** Served past its TTL while the worker refreshes it behind the response. */
  stale?: boolean;
  total?: number;
}

export interface CachedResponse<T> {
  data: T;
  meta: CacheMeta;
}

/**
 * What a paginated list page asks for in one go.
 *
 * These reads are served from the worker's cached copy of the tenant's data —
 * it holds the whole list and slices to `limit`, so asking for the lot costs a
 * larger response and nothing else. Paging is then instant and client-side,
 * and a 900-user tenant is actually reachable past page two. Routes clamp to
 * their own ceiling (999 for Graph lists), so this is a request, not a promise.
 */
export const LIST_LIMIT = 999;

/** Per-call options for cached endpoints. */
export interface FetchOptions {
  /** Bypass the worker's cache and pull live from Graph. Backs the Refresh buttons. */
  refresh?: boolean;
}

/** Append `refresh=1` without trampling a path that already carries a query string. */
export function withRefresh(path: string, opts?: FetchOptions): string {
  if (!opts?.refresh) return path;
  return `${path}${path.includes('?') ? '&' : '?'}refresh=1`;
}

async function rawFetch<T>(path: string, options?: RequestInit): Promise<APIResponse<T>> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (res.status === 401) {
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth/login')) {
      window.location.href = '/auth/login';
    }
    throw new APIError('AUTH_REQUIRED', 'Authentication required');
  }

  // Never res.json() blindly: timeouts, crash pages, and auth interstitials
  // arrive as HTML and would throw a cryptic JSON parse error at the user.
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.toLowerCase().includes('application/json')) {
    const body = await res.text().catch(() => '');
    throw new APIError(`HTTP_${res.status}`, describeNonJsonResponse(res.status, body));
  }

  let data: APIResponse<T>;
  try {
    data = await res.json() as APIResponse<T>;
  } catch {
    throw new APIError(`HTTP_${res.status}`, describeNonJsonResponse(res.status, ''));
  }

  if (!data.success) {
    throw new APIError(
      data.error?.code ?? 'UNKNOWN',
      data.error?.message ?? 'An error occurred',
      data.error?.details
    );
  }

  return data;
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await rawFetch<T>(path, options);
  return response.data as T;
}

/** Fetch a cached endpoint, keeping the cache provenance the page needs to display. */
async function apiFetchCached<T>(path: string, opts?: FetchOptions): Promise<CachedResponse<T>> {
  const response = await rawFetch<T>(withRefresh(path, opts));
  return { data: response.data as T, meta: (response.meta ?? {}) as CacheMeta };
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  /**
   * GET an endpoint the worker caches. Returns the payload plus its age, and
   * with `{ refresh: true }` forces the worker to go to Graph.
   */
  getCached: <T>(path: string, opts?: FetchOptions) => apiFetchCached<T>(path, opts),
  post: <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
